import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Copy } from 'lucide-react';
import { fetchProductById } from '../api';
import { Product as ProductFinal, ScriptItem } from '../api';
import axios from 'axios';
import {
  formatCurrency,
  getTotalCost,
  getTotalPrice,
  getTotalDeliveryTime
} from './utils';
import { secondsToHHmm } from '../../../../utils/time';
import ModalTest from '../../../../components/BaseModal';
import PricesDisplay from '../../../../components/PricesDisplay';
import {
  Button,
  ButtonContainer,
  CloseDiv,
  ContainerOfModal,
  Header,
  HeaderTitle,
  ViewContainer,
  ViewItem
} from '../../../../components/styles/modal-style';
import { Label } from '../../../../components/styles/select-style';
import editPen from '../../../../assets/certificates/Tables/pencil.svg';
import {
  LoadingContainer,
  ErrorContainer,
  NotFoundContainer,
  HeaderActions,
  EditButtonContainer,
  EditIcon,
  ScriptsContainer,
  ScriptsTitle,
  ScriptCard,
  ScriptName,
  ScriptNameLabel,
  AdditionalDeadline,
  ScriptSummary,
  ItemCard,
  StepNumber,
  StepLabel,
  NoItemsMessage
} from './style';

const ViewProduct: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: product, isLoading, isError, error } = useQuery<ProductFinal, Error>({
    queryKey: ['product', productId],
    queryFn: () => fetchProductById(productId!),
    enabled: !!productId,
  });

  const getItemTypeLabel = (type: string) => {
    switch (type) {
      case 'rawMaterial': return 'Matéria Prima';
      case 'service': return 'Serviço';
      case 'resource': return 'Insumo';
      case 'productFinal': return 'Produto Manufaturado';
      default: return type;
    }
  };

  const getItemDisplayName = (item: ScriptItem) => {
    switch (item.type) {
      case 'rawMaterial': return item.rawMaterialName || 'N/A';
      case 'service': return item.serviceName || 'N/A';
      case 'resource': return item.resourceName || 'N/A';
      case 'productFinal': return item.productFinalName || 'N/A';
      default: return 'N/A';
    }
  };

  const getItemUnitName = (item: ScriptItem) => {
    if (!item.unitOfMeasurementId || !item.prices) {
      return null;
    }

    const matchedPrice = item.prices.find(price => price.unitOfMeasurement?.id === item.unitOfMeasurementId);
    return matchedPrice?.unitOfMeasurement?.name || null;
  };

  const getItemQuantityLabel = (item: ScriptItem) => {
    const quantityValue = item.weight ?? item.quantity;

    if (quantityValue === undefined || quantityValue === null) {
      return 'Conforme especificação';
    }

    const unitName = getItemUnitName(item);
    return unitName ? `${quantityValue} ${unitName}` : `${quantityValue}`;
  };

  const handleDuplicate = () => {
    if (!product) return;

    const duplicateData = {
      name: `${product.name} - Cópia`,
      norm: product.norm,
      quantity: product.quantity,
      scripts: product.scripts?.map((script: any) => ({
        ...script,
        id: Date.now() + Math.random(),
        name: script.name,
        items: script.items?.map((item: any, index: number) => ({
          ...item,
          id: Date.now() + Math.random() + index,
          order: index + 1,
        })) || []
      })) || []
    };

    navigate('/panel/product-final/create', { 
      state: { duplicateData } 
    });
  };

  if (isLoading) {
    return <LoadingContainer>Carregando...</LoadingContainer>;
  }

  if (isError) {
    return <ErrorContainer>Erro ao carregar produto: {error?.message}</ErrorContainer>;
  }

  if (!product) {
    return <NotFoundContainer>Produto não encontrado.</NotFoundContainer>;
  }

  const allItems = product.scripts?.flatMap(script => script.items || []) || [];

  return (
    <ModalTest>
      <ContainerOfModal width='800px'>
        <Header>
          <HeaderTitle>Produto Final: {product.name}</HeaderTitle>
          <HeaderActions>
            <EditButtonContainer
              onClick={handleDuplicate}
              className='duplicate'
              style={{ marginRight: '8px' }}
            >
              <Copy size={16} style={{ marginRight: '4px' }} />
              Copiar
            </EditButtonContainer>
            <EditButtonContainer
              onClick={() => navigate(`/panel/product-final/edit/${product.id}`)}
              className='cancel'
            >
              <EditIcon src={editPen} alt='edit' />
              Editar
            </EditButtonContainer>
            <CloseDiv onClick={() => navigate('/panel/product-final')}>
              <X size={20} />
            </CloseDiv>
          </HeaderActions>
        </Header>

        <ViewContainer>
          <ViewItem>
            <span>Nome do Produto</span>
            <Label>{product.name}</Label>
          </ViewItem>
          <ViewItem>
            <span>Norma</span>
            <Label>{product.norm || 'Não especificada'}</Label>
          </ViewItem>
          <ViewItem>
            <span>Quantidade</span>
            <Label>{product.quantity || 'Não especificada'}</Label>
          </ViewItem>
          <ViewItem>
            <span>Data de Criação</span>
            <Label>{product.createdAt ? new Date(product.createdAt).toLocaleDateString('pt-BR') : 'N/A'}</Label>
          </ViewItem>
        </ViewContainer>

        {product.scripts && product.scripts.length > 0 && (
          <ScriptsContainer>
            <ScriptsTitle>
              Scripts
            </ScriptsTitle>
            {product.scripts
              .sort((a, b) => a.id - b.id)
              .map((script, scriptIndex) => (
                <ScriptCard key={script.id || scriptIndex}>
                  <ScriptName>
                    <ScriptNameLabel>
                      Nome do script: {script.name}
                      {script.additionalDeadline && script.additionalDeadline > 0 && (
                        <AdditionalDeadline>
                          (+{script.additionalDeadline} dias)
                        </AdditionalDeadline>
                      )}
                    </ScriptNameLabel>
                  </ScriptName>

                  <ScriptSummary>
                    <ViewItem>
                      <span>Prazo Total</span>
                      <Label>{(script.additionalDeadline || 0) + (script.totalDeliveryTime || 0)} dias</Label>
                    </ViewItem>
                    <ViewItem>
                      <span>Custo Total</span>
                      <Label>{formatCurrency(script.cost || 0)}</Label>
                    </ViewItem>
                    <ViewItem>
                      <span>Preço Total</span>
                      <Label>{formatCurrency((script.cost || 0) * 1.3)}</Label>
                    </ViewItem>
                  </ScriptSummary>

                  {script.items && script.items.length > 0 ? (
                    <ViewContainer>
                      {script.items.map((item, itemIndex) => (
                        <ItemCard key={item.id || itemIndex}>
                          <StepNumber>
                            <StepLabel>Passo {itemIndex + 1}</StepLabel>
                          </StepNumber>
                          <ViewItem>
                            <span>Tipo</span>
                            <Label>{getItemTypeLabel(item.type)}</Label>
                          </ViewItem>
                          <ViewItem>
                            <span>Item</span>
                            <Label>{getItemDisplayName(item)}</Label>
                          </ViewItem>
                          {item.type === 'service' && (
                            <>
                              <ViewItem>
                                <span>Fornecedor</span>
                                <Label>{item.company?.name || item.companyName || 'N/A'}</Label>
                              </ViewItem>
                              {item.company?.cnpj && (
                                <ViewItem>
                                  <span>CNPJ</span>
                                  <Label>{item.company.cnpj}</Label>
                                </ViewItem>
                              )}
                              <ViewItem>
                                <span>Preços</span>
                                <Label>
                                  <PricesDisplay
                                    prices={item.prices || [] as any}
                                    maxWidth={300}
                                    selectedUnitId={item.unitOfMeasurementId}
                                  />
                                </Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Tempo de Execução</span>
                                <Label>{secondsToHHmm(item.executionTime as any) || 'N/A'}</Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Tempo de Setup</span>
                                <Label>{secondsToHHmm((item as any).setupTime) || 'N/A'}</Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Qualificação</span>
                                <Label>{item.requiredQualification || 'N/A'}</Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Prazo</span>
                                <Label>{item.deliveryTime ? `${item.deliveryTime} dias` : 'N/A'}</Label>
                              </ViewItem>
                              {item.description && (
                              <ViewItem>
                                <span>Descrição</span>
                                <Label>{item.description}</Label>
                              </ViewItem>
                            )}
                            {item.observation && (
                              <ViewItem>
                                <span>Observação</span>
                                <Label>{item.observation}</Label>
                              </ViewItem>
                            )}
                              {item.machineName && (
                                <>
                                  <ViewItem>
                                    <span>Máquina</span>
                                    <Label>{item.machineName} ({item.machineCode})</Label>
                                  </ViewItem>
                                  <ViewItem>
                                    <span>Capacidade</span>
                                    <Label>{item.machineCapacity || 'N/A'}</Label>
                                  </ViewItem>
                                  <ViewItem>
                                    <span>Custo/Hora</span>
                                    <Label>{item.machineCostPerHour ? formatCurrency(item.machineCostPerHour) : 'N/A'}</Label>
                                  </ViewItem>
                                </>
                              )}
                              <ViewItem>
                                <span>Custo do Item</span>
                                <Label>{item.cost ? formatCurrency(item.cost) : 'N/A'}</Label>
                              </ViewItem>
                            </>
                          )}
                          {item.type === 'rawMaterial' && (
                            <>
                              <ViewItem>
                                <span>Peso</span>
                                <Label>{item.weight ? `${item.weight} kg` : 'N/A'}</Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Medidas</span>
                                <Label>{item.idealMeasurements || 'N/A'}</Label>
                              </ViewItem>
                            </>
                          )}
                          {item.type === 'resource' && (
                            <>
                              <ViewItem>
                                <span>Preços</span>
                                <Label>
                                  <PricesDisplay
                                    prices={item.prices || [] as any}
                                    maxWidth={300}
                                    selectedUnitId={item.unitOfMeasurementId}
                                  />
                                </Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Quantidade</span>
                                <Label>{getItemQuantityLabel(item)}</Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Custo do Item</span>
                                <Label>{item.cost ? formatCurrency(item.cost) : 'N/A'}</Label>
                              </ViewItem>
                            </>
                          )}
                          {item.type === 'productFinal' && (
                            <>
                              <ViewItem>
                                <span>Script</span>
                                <Label>
                                  {item.productScriptName ?
                                    `${item.productScriptName} - (${item.productScriptItems?.map(subItem => `${getItemDisplayName(subItem)} (${getItemTypeLabel(subItem.type)})`).join(' → ') || 'Sem itens'})`
                                    : 'N/A'}
                                </Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Quantidade</span>
                                <Label>{getItemQuantityLabel(item)}</Label>
                              </ViewItem>
                              <ViewItem>
                                <span>Custo do Item</span>
                                <Label>{item.cost ? formatCurrency(item.cost) : 'N/A'}</Label>
                              </ViewItem>
                            </>
                          )}
                        </ItemCard>
                      ))}
                    </ViewContainer>
                  ) : (
                    <NoItemsMessage>
                      Este script não possui itens.
                    </NoItemsMessage>
                  )}
                </ScriptCard>
              ))}
          </ScriptsContainer>
        )}

      </ContainerOfModal>
    </ModalTest>
  );
};

export default ViewProduct;